import { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "@tanstack/react-router"
import { post, get } from "../api"
import { Switch } from "@headlessui/react"
import { CheckIcon, BoltIcon, InformationCircleIcon, QueueListIcon, ArrowUpIcon, ArrowDownIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { Button } from '../app/ui';
import FAQ from '../components/FAQ';

// Pricing plans
const plans = [
    {
        id: 'starter',
        name: 'Starter',
        description: 'Perfect for hobbyists and experimentation',
        credits: 500,
        models: '~33',
        monthly: {
            price: 19,
            lookupKey: 'starter_monthly',
        },
        annual: {
            price: 15,
            lookupKey: 'starter_annual',
            billAmount: 180
        }
    },
    {
        id: 'pro',
        name: 'Pro',
        description: 'For creators and indie developers',
        credits: 1500,
        models: '~100',
        popular: true,
        monthly: {
            price: 49,
            lookupKey: 'pro_monthly',
        },
        annual: {
            price: 39,
            lookupKey: 'pro_annual',
            billAmount: 468
        }
    },
    {
        id: 'business',
        name: 'Business',
        description: 'For studios and high-volume needs',
        credits: 5000,
        models: '~330',
        monthly: {
            price: 99,
            lookupKey: 'business_monthly',
        },
        annual: {
            price: 79,
            lookupKey: 'business_annual',
            billAmount: 948
        }
    }
]

// Helper to determine current plan from subscription
const getCurrentPlanInfo = (subscription) => {
    if (!subscription) return null

    const monthlyPrice = subscription.monthly_price
    const isAnnual = subscription.plan_type === 'ANNUAL'

    // Match based on monthly price
    for (const plan of plans) {
        const planPrice = isAnnual ? plan.annual.price : plan.monthly.price
        if (Math.abs(planPrice - monthlyPrice) < 1) {
            return {
                plan,
                isAnnual,
                lookupKey: isAnnual ? plan.annual.lookupKey : plan.monthly.lookupKey
            }
        }
    }
    return null
}

// Determine if a plan is an upgrade, downgrade, or current
const getPlanAction = (plan, billingInterval, currentPlanInfo) => {
    if (!currentPlanInfo) return 'subscribe'

    const targetLookupKey = billingInterval === 'annual' ? plan.annual.lookupKey : plan.monthly.lookupKey

    // Check if same plan and billing interval
    if (targetLookupKey === currentPlanInfo.lookupKey) {
        return 'current'
    }

    // Compare by credits (plan tier)
    const currentCredits = currentPlanInfo.plan.credits
    const targetCredits = plan.credits

    if (targetCredits > currentCredits) {
        return 'upgrade'
    } else if (targetCredits < currentCredits) {
        return 'downgrade'
    } else {
        // Same tier, different billing interval
        const currentIsAnnual = currentPlanInfo.isAnnual
        const targetIsAnnual = billingInterval === 'annual'

        if (targetIsAnnual && !currentIsAnnual) {
            return 'upgrade' // Monthly to annual is an upgrade (commitment)
        } else {
            return 'downgrade' // Annual to monthly is a downgrade
        }
    }
}

// Stats to display
const stats = [
    { label: "Happy users", value: "5,500+" },
    { label: "Models processed", value: "50,000+" },
    { label: "Uptime", value: "99.9%" },
    { label: "Success rate", value: "99%" },
]

// Value props
const features = [
    {
        image: "/images/assembly-line.png",
        title: "Production ready",
        desc: "High-quality topology and textures suitable for games and AR/VR.",
    },
    {
        image: "/images/papers-stack.png",
        title: "Commercial license",
        desc: "You own 100% of the assets you generate. Use them anywhere.",
    },
    {
        image: "/images/quickness.png",
        title: "Standard formats",
        desc: "Export directly to GLB/GLTF, compatible with all major 3D tools.",
    },
    {
        image: "/images/paycheck.png",
        title: "Flexible billing",
        desc: "Upgrade, downgrade, or cancel your plan at any time.",
    },
]

export default function AiStudioBilling() {
    const [subscription, setSubscription] = useState(null)
    const [transactions, setTransactions] = useState([])
    const [loading, setLoading] = useState(true)
    const [checkingOut, setCheckingOut] = useState(false)
    const [billingInterval, setBillingInterval] = useState('annual')
    const [pollingForSubscription, setPollingForSubscription] = useState(false)
    const [confirmModal, setConfirmModal] = useState(null) // { plan, action, preview }
    const [processingChange, setProcessingChange] = useState(false)
    const pollingRef = useRef(null)
    const navigate = useNavigate()

    // Check for success query param
    const searchParams = new URLSearchParams(window.location.search)
    const isSuccess = searchParams.get('success') === 'true'

    // Get current plan info for comparison
    const currentPlanInfo = getCurrentPlanInfo(subscription)

    const pollForSubscription = useCallback(async () => {
        const startTime = Date.now()
        const maxDuration = 10000 // 10 seconds
        const pollInterval = 1000 // 1 second

        const poll = async () => {
            try {
                const usageData = await get('/api/1/user/usage')
                if (usageData.has_active_subscription) {
                    // Subscription found! Load full data and stop polling
                    setPollingForSubscription(false)
                    await loadSubscriptionData()
                    // Clear the success param from URL
                    navigate({ to: '/app/ai-studio/billing', replace: true })
                    return
                }
            } catch (error) {
                console.error('Polling error:', error)
            }

            // Continue polling if within time limit
            if (Date.now() - startTime < maxDuration) {
                pollingRef.current = setTimeout(poll, pollInterval)
            } else {
                // Timeout - stop polling and load whatever we have
                setPollingForSubscription(false)
                await loadSubscriptionData()
                navigate({ to: '/app/ai-studio/billing', replace: true })
            }
        }

        poll()
    }, [navigate])

    useEffect(() => {
        if (isSuccess) {
            // Start polling for subscription after successful checkout
            setPollingForSubscription(true)
            pollForSubscription()
        } else {
            loadSubscriptionData()
        }

        return () => {
            if (pollingRef.current) {
                clearTimeout(pollingRef.current)
            }
        }
    }, [isSuccess, pollForSubscription])

    const loadSubscriptionData = async () => {
        try {
            // Load subscription
            const subData = await get('/api/1/ai-studio-billing/subscription')
            setSubscription(subData.subscription)

            // Load transactions if subscribed
            if (subData.subscription) {
                const txData = await get('/api/1/ai-studio-billing/transactions')
                setTransactions(txData.transactions || [])
            }
        } catch (error) {
            console.error('Failed to load subscription data:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSubscribe = async (plan) => {
        if (checkingOut) return

        setCheckingOut(true)
        try {
            const lookupKey = billingInterval === 'monthly' ? plan.monthly.lookupKey : plan.annual.lookupKey
            const response = await post('/api/1/ai-studio-billing/create-checkout-session', {
                price_lookup_key: lookupKey
            })

            if (response && response.checkout_url) {
                window.location.href = response.checkout_url
            } else {
                alert('Failed to create checkout session')
                setCheckingOut(false)
            }
        } catch (error) {
            console.error('Checkout error:', error)
            alert('Failed to start checkout. Please try again.')
            setCheckingOut(false)
        }
    }

    const handlePlanAction = async (plan) => {
        const action = getPlanAction(plan, billingInterval, currentPlanInfo)

        if (action === 'current') return

        if (action === 'subscribe') {
            handleSubscribe(plan)
            return
        }

        // For upgrade/downgrade, show preview first
        setProcessingChange(true)
        try {
            const lookupKey = billingInterval === 'monthly' ? plan.monthly.lookupKey : plan.annual.lookupKey
            const preview = await post('/api/1/ai-studio-billing/preview-plan-change', {
                price_lookup_key: lookupKey
            })

            if (preview.error) {
                alert(preview.error)
                return
            }

            setConfirmModal({
                plan,
                action,
                lookupKey,
                preview
            })
        } catch (error) {
            console.error('Preview error:', error)
            alert('Failed to preview plan change. Please try again.')
        } finally {
            setProcessingChange(false)
        }
    }

    const confirmPlanChange = async () => {
        if (!confirmModal) return

        setProcessingChange(true)
        try {
            const response = await post('/api/1/ai-studio-billing/change-plan', {
                price_lookup_key: confirmModal.lookupKey
            })

            if (response.error) {
                alert(response.error)
                return
            }

            // Refresh subscription data
            await loadSubscriptionData()
            setConfirmModal(null)

            // Show success message based on action
            if (response.is_upgrade) {
                alert('🎉 ' + response.message)
            } else {
                alert('✓ ' + response.message)
            }
        } catch (error) {
            console.error('Plan change error:', error)
            alert('Failed to change plan. Please try again.')
        } finally {
            setProcessingChange(false)
        }
    }

    const handleCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.')) {
            return
        }

        try {
            const response = await post('/api/1/ai-studio-billing/cancel', {})
            if (response && response.success) {
                alert(`Subscription will be cancelled on ${new Date(response.period_end).toLocaleDateString()}`)
                loadSubscriptionData()
            }
        } catch (error) {
            console.error('Cancellation error:', error)
            alert('Failed to cancel subscription')
        }
    }

    if (loading || pollingForSubscription) {
        return (
            <div className="flex flex-col justify-center items-center min-h-screen gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
                {pollingForSubscription && (
                    <div className="text-center">
                        <p className="text-lg font-medium text-foreground">Activating your subscription...</p>
                        <p className="text-sm text-muted-foreground mt-1">This may take a few seconds</p>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="space-y-20 max-w-7xl mx-auto px-6 py-12 font-sans">

            {/* Confirmation Modal */}
            {confirmModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !processingChange && setConfirmModal(null)} />
                    <div className="relative bg-background border border-border/50 rounded-3xl p-8 max-w-md w-full shadow-2xl">
                        <button
                            onClick={() => !processingChange && setConfirmModal(null)}
                            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
                            disabled={processingChange}
                        >
                            <XMarkIcon className="w-6 h-6" />
                        </button>

                        <div className="text-center mb-6">
                            {confirmModal.action === 'upgrade' ? (
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
                                    <ArrowUpIcon className="w-8 h-8 text-green-500" />
                                </div>
                            ) : (
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/10 mb-4">
                                    <ArrowDownIcon className="w-8 h-8 text-orange-500" />
                                </div>
                            )}
                            <h3 className="text-xl font-black uppercase tracking-tight text-foreground">
                                {confirmModal.action === 'upgrade' ? 'Upgrade Plan' : 'Downgrade Plan'}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-2 font-mono">
                                {confirmModal.plan.name} • {confirmModal.preview.new_credits} credits/month
                            </p>
                        </div>

                        <div className="bg-background/50 border border-border/50 rounded-xl p-4 mb-6">
                            {confirmModal.action === 'upgrade' ? (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-muted-foreground">Charge today</span>
                                        <span className="text-lg font-bold text-foreground">${confirmModal.preview.amount_due?.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-muted-foreground">New credits</span>
                                        <span className="text-lg font-bold text-green-500">{confirmModal.preview.new_credits}</span>
                                    </div>
                                    <div className="pt-3 border-t border-border/50">
                                        <p className="text-xs text-muted-foreground font-mono leading-relaxed">
                                            {confirmModal.preview.message}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-muted-foreground">Effective date</span>
                                        <span className="text-lg font-bold text-foreground">
                                            {new Date(confirmModal.preview.effective_date).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-muted-foreground">New credits</span>
                                        <span className="text-lg font-bold text-orange-500">{confirmModal.preview.new_credits}</span>
                                    </div>
                                    <div className="pt-3 border-t border-border/50">
                                        <p className="text-xs text-muted-foreground font-mono leading-relaxed">
                                            {confirmModal.preview.message}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1 py-4 text-xs font-bold uppercase tracking-widest"
                                onClick={() => setConfirmModal(null)}
                                disabled={processingChange}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant={confirmModal.action === 'upgrade' ? 'primary' : 'secondary'}
                                className={`flex-1 py-4 text-xs font-bold uppercase tracking-widest ${confirmModal.action === 'upgrade' ? 'bg-green-600 hover:bg-green-500' : 'bg-orange-600 hover:bg-orange-500'}`}
                                onClick={confirmPlanChange}
                                disabled={processingChange}
                            >
                                {processingChange ? 'Processing...' : confirmModal.action === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Hero Section - only for non-subscribers */}
            {!subscription && (
                <div className="text-center max-w-4xl mx-auto relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/20 blur-[100px] rounded-full pointer-events-none"></div>
                    <h1 className="relative text-5xl md:text-7xl font-black text-foreground tracking-tighter mb-6 uppercase">
                        Unlock <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">Potential</span>
                    </h1>
                    <p className="relative text-xs md:text-sm font-mono text-muted-foreground max-w-2xl mx-auto uppercase tracking-widest mb-12">
                        Professional Grade 3D Generation Infrastructure
                    </p>
                </div>
            )}

            {/* Stats - only for non-subscribers */}
            {!subscription && (
                <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
                    {stats.map((s) => (
                        <div key={s.label} className="bg-background/30 backdrop-blur-sm border border-border/50 rounded-2xl p-6 text-center hover:border-purple-500/30 transition-all duration-300 group">
                            <div className="text-3xl font-light text-foreground mb-2 group-hover:scale-110 transition-transform">{s.value}</div>
                            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{s.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pricing Section - show for everyone */}
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-2xl font-bold uppercase tracking-tight text-foreground mb-8">
                        {subscription ? 'Change Your Plan' : 'Select Access Level'}
                    </h2>

                    {/* Billing Toggle */}
                    <div className="inline-flex items-center justify-center gap-6 bg-background/50 backdrop-blur-md p-2 rounded-full border border-border/50">
                        <span className={`text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors ${billingInterval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`} onClick={() => setBillingInterval('monthly')}>Monthly</span>
                        <Switch
                            checked={billingInterval === 'annual'}
                            onChange={() => setBillingInterval(billingInterval === 'monthly' ? 'annual' : 'monthly')}
                            className={`${billingInterval === 'annual' ? 'bg-purple-600' : 'bg-gray-700'} relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none`}
                        >
                            <span className={`${billingInterval === 'annual' ? 'translate-x-7' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                        </Switch>
                        <span className={`text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors ${billingInterval === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`} onClick={() => setBillingInterval('annual')}>
                            Annual <span className="text-purple-500 ml-1">(2 months free)</span>
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                    {plans.map((plan) => {
                        const price = billingInterval === 'monthly' ? plan.monthly.price : plan.annual.price
                        const action = getPlanAction(plan, billingInterval, currentPlanInfo)
                        const isCurrent = action === 'current'
                        const isUpgrade = action === 'upgrade'
                        const isDowngrade = action === 'downgrade'

                        return (
                            <div
                                key={plan.id}
                                className={`relative flex flex-col rounded-3xl p-8 transition-all duration-500 ${isCurrent
                                    ? 'bg-purple-500/10 backdrop-blur-xl border-2 border-purple-500 shadow-2xl shadow-purple-500/20'
                                    : plan.popular && !subscription
                                        ? 'bg-background/80 backdrop-blur-xl border-2 border-purple-500/50 shadow-2xl shadow-purple-500/20 scale-105 z-10'
                                        : 'bg-background/30 backdrop-blur-md border border-border/50 hover:border-border hover:bg-background/50'
                                    }`}
                            >
                                {isCurrent && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <span className="bg-purple-600 text-white text-[10px] font-bold px-4 py-1 rounded-full shadow-lg uppercase tracking-widest flex items-center gap-1">
                                            <CheckIcon className="w-3 h-3" /> Current Plan
                                        </span>
                                    </div>
                                )}
                                {!isCurrent && plan.popular && !subscription && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <span className="bg-purple-600 text-white text-[10px] font-bold px-4 py-1 rounded-full shadow-lg uppercase tracking-widest">
                                            Recommended
                                        </span>
                                    </div>
                                )}

                                <div className="mb-8 text-center">
                                    <h3 className="text-lg font-black uppercase tracking-widest text-foreground mb-2">{plan.name}</h3>
                                    <p className="text-xs text-muted-foreground h-8 font-mono">{plan.description}</p>
                                </div>

                                <div className="mb-8 text-center">
                                    <div className="flex items-baseline justify-center gap-1">
                                        <span className="text-5xl font-light text-foreground">${price}</span>
                                        <span className="text-muted-foreground text-sm font-mono">/mo</span>
                                    </div>
                                    {billingInterval === 'annual' && (
                                        <p className="text-[10px] text-muted-foreground mt-2 font-mono uppercase">
                                            Billed ${plan.annual.billAmount} yearly
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-4 mb-8 flex-grow">
                                    <div className="flex items-center gap-3">
                                        <div className="p-1 rounded-full bg-purple-500/10 text-purple-500">
                                            <BoltIcon className="w-4 h-4" />
                                        </div>
                                        <span className="font-mono text-xs uppercase tracking-wide font-bold text-foreground">{plan.credits} credits</span>
                                        <Tooltip
                                            show={true}
                                            text={
                                                <div className="text-left space-y-1 p-1 font-mono text-[10px]">
                                                    <div>Image → 3D: 15 credits</div>
                                                    <div>Text → 3D: 15 credits</div>
                                                    <div>Refine model: 15 credits</div>
                                                </div>
                                            }
                                        >
                                            <InformationCircleIcon className="w-4 h-4 text-muted-foreground cursor-help" />
                                        </Tooltip>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="p-1 rounded-full bg-purple-500/10 text-purple-500">
                                            <QueueListIcon className="w-4 h-4" />
                                        </div>
                                        <span className="font-mono text-xs uppercase tracking-wide text-foreground">Standard queue priority</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="p-1 rounded-full bg-green-500/10 text-green-500">
                                            <CheckIcon className="w-4 h-4" />
                                        </div>
                                        <span className="font-mono text-xs uppercase tracking-wide text-foreground">Commercial license</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="p-1 rounded-full bg-green-500/10 text-green-500">
                                            <CheckIcon className="w-4 h-4" />
                                        </div>
                                        <span className="font-mono text-xs uppercase tracking-wide text-foreground">Private generation</span>
                                    </div>
                                </div>

                                {isCurrent ? (
                                    <Button
                                        variant="outline"
                                        className="w-full py-6 text-xs font-bold uppercase tracking-widest border-purple-500/50 text-purple-500 cursor-default"
                                        disabled
                                    >
                                        <CheckIcon className="w-4 h-4 mr-2" /> Current Plan
                                    </Button>
                                ) : isUpgrade ? (
                                    <Button
                                        variant="primary"
                                        className="w-full py-6 text-xs font-bold uppercase tracking-widest bg-green-600 hover:bg-green-500 shadow-lg shadow-green-500/20"
                                        onClick={() => handlePlanAction(plan)}
                                        disabled={checkingOut || processingChange}
                                    >
                                        {processingChange ? 'Processing...' : (
                                            <>
                                                <ArrowUpIcon className="w-4 h-4 mr-2" /> Upgrade
                                            </>
                                        )}
                                    </Button>
                                ) : isDowngrade ? (
                                    <Button
                                        variant="secondary"
                                        className="w-full py-6 text-xs font-bold uppercase tracking-widest hover:bg-foreground/5"
                                        onClick={() => handlePlanAction(plan)}
                                        disabled={checkingOut || processingChange}
                                    >
                                        {processingChange ? 'Processing...' : (
                                            <>
                                                <ArrowDownIcon className="w-4 h-4 mr-2" /> Downgrade
                                            </>
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        variant={plan.popular ? "primary" : "secondary"}
                                        className={`w-full py-6 text-xs font-bold uppercase tracking-widest ${plan.popular ? "shadow-lg shadow-purple-500/20" : "hover:bg-foreground/5"}`}
                                        onClick={() => handlePlanAction(plan)}
                                        disabled={checkingOut}
                                    >
                                        {checkingOut ? 'Processing...' : 'Get Access'}
                                    </Button>
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* Subtle Link */}
                <div className="mt-16 text-center">
                    <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                        Need file compression?{' '}
                        <a href="/app/billing" className="text-purple-500 hover:text-purple-400 hover:underline transition-colors ml-2">
                            View Compression Plans →
                        </a>
                    </p>
                </div>
            </div>

            {/* Value Props - only for non-subscribers */}
            {!subscription && (
                <div className="max-w-6xl mx-auto">
                    <h3 className="text-2xl font-bold text-foreground text-center mb-12 uppercase tracking-tight">System Capabilities</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((f) => (
                            <div key={f.title} className="flex flex-col items-center text-center gap-4 p-8 bg-background/30 backdrop-blur-sm border border-border/50 rounded-2xl hover:border-purple-500/30 transition-all duration-300 group">
                                <div className="flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                                    {f.image ? (
                                        <img src={f.image} alt={f.title} className="w-16 h-16 object-contain" />
                                    ) : (
                                        <f.icon className="w-12 h-12 text-purple-500" />
                                    )}
                                </div>
                                <div>
                                    <div className="font-bold text-foreground mb-2 text-sm uppercase tracking-wide">{f.title}</div>
                                    <div className="text-xs text-muted-foreground font-mono leading-relaxed">{f.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* FAQ - only for non-subscribers */}
            {!subscription && <FAQ />}

            {/* Existing Subscription Display */}
            {subscription && subscription.status === 'ACTIVE' && (
                <div className="max-w-6xl mx-auto mb-16">
                    <div className="bg-background/30 backdrop-blur-md border border-border/50 rounded-3xl p-8 shadow-xl">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h2 className="text-2xl font-black uppercase tracking-tighter text-foreground">Active Membership</h2>
                                <p className="text-xs font-mono text-muted-foreground mt-1 uppercase tracking-widest">
                                    {subscription.plan_type === 'ANNUAL' ? 'Annual Plan' : 'Monthly Plan'}
                                </p>
                            </div>
                            <div className="text-right">
                                <div className="text-4xl font-light text-foreground">{subscription.ai_studio_credits}</div>
                                <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">credits remaining</div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="bg-background/50 border border-border/50 rounded-xl p-6">
                                <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Next billing date</div>
                                <div className="text-xl font-bold text-foreground">
                                    {new Date(subscription.current_period_end).toLocaleDateString()}
                                </div>
                            </div>
                            <div className="bg-background/50 border border-border/50 rounded-xl p-6">
                                <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Monthly price</div>
                                <div className="text-xl font-bold text-foreground">
                                    ${subscription.monthly_price.toFixed(2)}
                                </div>
                            </div>
                        </div>

                        {!subscription.cancel_at_period_end && (
                            <Button variant="outline" onClick={handleCancel} className="w-full border-red-500/30 text-red-500 hover:bg-red-500/10 hover:border-red-500/50 uppercase text-xs font-bold tracking-widest">
                                Cancel subscription
                            </Button>
                        )}

                        {subscription.cancel_at_period_end && (
                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-yellow-500 text-sm font-mono text-center">
                                TERMINATION SCHEDULED: {new Date(subscription.current_period_end).toLocaleDateString()}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Transaction History */}
            {subscription && transactions.length > 0 && (
                <div className="max-w-6xl mx-auto">
                    <h3 className="text-xl font-bold text-foreground mb-6 uppercase tracking-tight">Transaction Log</h3>
                    <div className="bg-background/30 backdrop-blur-sm border border-border/50 rounded-2xl overflow-hidden">
                        <div className="divide-y divide-border/50">
                            <div className="grid grid-cols-12 gap-4 px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-black/20">
                                <div className="col-span-4">Date</div>
                                <div className="col-span-3">Amount</div>
                                <div className="col-span-3">Credits</div>
                                <div className="col-span-2">Status</div>
                            </div>
                            {transactions.map((tx) => (
                                <div key={tx.id} className="grid grid-cols-12 gap-4 px-6 py-4 text-sm items-center font-mono hover:bg-white/5 transition-colors">
                                    <div className="col-span-4 text-foreground">
                                        {tx.created_at ? new Date(tx.created_at).toLocaleString() : '-'}
                                    </div>
                                    <div className="col-span-3 text-foreground">
                                        ${typeof tx.amount === 'number' ? tx.amount.toFixed(2) : '-'}
                                    </div>
                                    <div className="col-span-3 text-foreground">{tx.credits_allocated ?? '-'}</div>
                                    <div className="col-span-2">
                                        <span className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${tx.status === 'paid' ? 'bg-green-500/10 text-green-500 border-green-500/30' :
                                            tx.status === 'failed' ? 'bg-red-500/10 text-red-500 border-red-500/30' :
                                                'bg-gray-500/10 text-gray-500 border-gray-500/30'
                                            }`}>
                                            {tx.status || '—'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Subscription - subtle at bottom */}
            {subscription && subscription.status === 'ACTIVE' && !subscription.cancel_at_period_end && (
                <div className="max-w-6xl mx-auto text-center pt-8 border-t border-border/20">
                    <button
                        onClick={handleCancel}
                        className="text-xs font-mono text-muted-foreground hover:text-red-500 transition-colors uppercase tracking-wider"
                    >
                        Cancel subscription
                    </button>
                    <p className="text-[10px] text-muted-foreground/50 mt-2 font-mono">
                        Your subscription will remain active until the end of your billing period
                    </p>
                </div>
            )}

            {subscription && subscription.cancel_at_period_end && (
                <div className="max-w-6xl mx-auto text-center pt-8 border-t border-border/20">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
                        <span className="text-xs font-mono text-yellow-500 uppercase tracking-wider">
                            Cancellation scheduled for {new Date(subscription.current_period_end).toLocaleDateString()}
                        </span>
                    </div>
                </div>
            )}
        </div>
    )
}

function Tooltip({ children, text, show }) {
    if (!show) return children;
    return (
        <div className="group relative flex items-center">
            {children}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-black/90 backdrop-blur-md border border-white/10 rounded-lg text-white text-xs z-50 shadow-xl">
                {text}
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-black/90"></div>
            </div>
        </div>
    );
}
